import React from 'react';
import { Box, Button, Popper, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  USER_HINT_MESSAGES,
  UserHintKey,
  dismissUserHint,
  getDismissedHints,
} from '../../helpers/userHints';

interface Props {
  hintKey: UserHintKey;
}

// One-time hint anchored to the element hosting it. Render inside the
// button/menu item the message is about; the popper points at it and
// spotlights it until dismissed.
function CoachMark({ hintKey }: Props) {
  const message = USER_HINT_MESSAGES[hintKey];
  // localStorage-backed; the query cache keeps mounted marks in sync
  // so dismissing one hides them all instantly
  const { data: dismissedHints } = useQuery({
    queryKey: ['user-hints'],
    queryFn: getDismissedHints,
    staleTime: Infinity,
  });
  const visible = !!dismissedHints && !dismissedHints.includes(hintKey);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = React.useState<HTMLElement | null>(null);
  // The menu/modal hosting the anchor animates into place; opening the
  // popper before that settles would pin it to a stale (0,0) position
  const [settled, setSettled] = React.useState(false);

  React.useEffect(() => {
    if (!anchorEl) return undefined;
    const timeout = setTimeout(() => setSettled(true), 300);
    return () => {
      clearTimeout(timeout);
      setSettled(false);
    };
  }, [anchorEl]);

  // Spotlight the element the mark is pointing at
  const theme = useTheme();
  React.useEffect(() => {
    const target = anchorEl?.parentElement;
    if (!target || !settled || !visible) return undefined;
    const originalStyle = target.style.cssText;
    target.style.backgroundColor = theme.palette.primary.light;
    target.style.borderRadius = '6px';
    return () => {
      target.style.cssText = originalStyle;
    };
  }, [anchorEl, settled, visible, theme]);

  if (!visible) return null;

  return (
    <>
      <span ref={setAnchorEl} />
      <Popper
        open={!!anchorEl && settled}
        // Anchor to the menu item / button hosting us, not the empty span
        anchorEl={() => anchorEl!.parentElement ?? anchorEl!}
        placement="top"
        modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'arrow', options: { element: arrowEl } },
        ]}
        sx={{ zIndex: theme.zIndex.tooltip }}>
        <Box
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          sx={{
            position: 'relative',
            backgroundColor: 'primary.main',
            color: 'common.white',
            p: 2,
            borderRadius: '10px',
            maxWidth: 300,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
          }}>
          <Box
            ref={setArrowEl}
            sx={{
              position: 'absolute',
              bottom: -6,
              width: 12,
              height: 12,
              // Popper positions this span; the rotated square is the arrow
              '&::before': {
                content: '""',
                display: 'block',
                width: 12,
                height: 12,
                backgroundColor: 'primary.main',
                transform: 'rotate(45deg)',
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              backgroundColor: '#fff',
              color: 'primary.main',
              borderRadius: '999px',
              px: 1.25,
              py: 0.25,
              mb: 1,
              fontWeight: 600,
            }}>
            New
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ color: 'common.white' }}
            mb={1.5}>
            {message}
          </Typography>
          <Button
            size="small"
            onClick={event => {
              event.stopPropagation();
              dismissUserHint(hintKey);
            }}
            sx={{
              color: 'common.white',
              textDecoration: 'underline',
              p: 0,
              minWidth: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              typography: 'h5',
            }}>
            Got it
          </Button>
        </Box>
      </Popper>
    </>
  );
}

export default CoachMark;
